import { z } from "zod";
import { AccountingReportCycle } from "@common/constants";
import { ClassFilterType } from "@common/types/class";
import { zQueryBoolean } from "@common/types/filters/general";
import { ReportFormat } from "@common/helpers/reports";
import {
    SchedulesReportMethod,
    SchedulesReportLayout,
    SchedulesReportRevenueSubMode,
    SchedulesReportRowKind,
    SchedulesReportRows,
} from "@common/types/reports/schedules-report";
import { RevenueSubMode } from "@common/types/domains/accounting/schedule";
import { reportPeriodWithLabelSchema } from "@common/schemas/domains/accounting/report.schema";
import {
    reportPresentationSchema,
    withRequiredPresentation,
} from "@common/schemas/domains/reportSettings/report-presentation.schema";
import { ReportSettingRowVisibility } from "@common/types/reports/report-settings";

export const schedulesReportFiltersSchema = z.object({
    templateIds: z.array(z.string().uuid()).optional(),
    counterpartyIds: z.array(z.string().uuid()).optional(),
    includeUnassignedCounterparty: zQueryBoolean().optional(),
    hasCounterparty: zQueryBoolean().optional(),
    holdingAccountIds: z.array(z.string().uuid()).optional(),
    groups: z.array(z.string()).optional(),
    termStartFrom: z.string().optional(),
    termStartTo: z.string().optional(),
    termEndFrom: z.string().optional(),
    termEndTo: z.string().optional(),
    classes: z.array(z.string()).optional(),
    classFilterType: z.nativeEnum(ClassFilterType).optional(),
    minAmount: z.coerce.number().optional(),
    maxAmount: z.coerce.number().optional(),
});

export const schedulesReportDateBoundsQuery = schedulesReportFiltersSchema
    .omit({ minAmount: true, maxAmount: true })
    .extend({
        entityId: z.coerce.number(),
        ledgerId: z.string().uuid(),
        // Restricts the report to these entities; defaults to the anchor entityId.
        entityIds: z.array(z.coerce.number()).optional(),
        revenueSubMode: z.nativeEnum(SchedulesReportRevenueSubMode).optional(),
        method: z.nativeEnum(SchedulesReportMethod).optional(),
        scheduleApplicationIds: z.array(z.string().uuid()).optional(),
    });
export type SchedulesReportDateBoundsQuery = z.infer<
    typeof schedulesReportDateBoundsQuery
>;

export const schedulesReportQuery = schedulesReportDateBoundsQuery
    .merge(schedulesReportFiltersSchema)
    .extend({
        startDate: z.string(),
        endDate: z.string(),
        cycle: z.nativeEnum(AccountingReportCycle).optional(),
        layout: z.nativeEnum(SchedulesReportLayout).optional(),
        rows: z.nativeEnum(SchedulesReportRows).optional(),
        includeApplications: zQueryBoolean()
            .optional()
            .describe(
                "Keeps per-schedule rows under the current / non-current split",
            ),
    });
export type SchedulesReportQuery = z.infer<typeof schedulesReportQuery>;

export const schedulesReportExportQuery = schedulesReportQuery.extend({
    format: z.nativeEnum(ReportFormat),
    rowVisibility: z.nativeEnum(ReportSettingRowVisibility).optional(),
});
export type SchedulesReportExportQuery = z.infer<
    typeof schedulesReportExportQuery
>;

export const schedulesRollforwardCellSchema = z.object({
    beginning: z.number(),
    additions: z.number(),
    recognized: z.number(),
    adjustments: z.number(),
    ending: z.number(),
});
export type SchedulesRollforwardCell = z.infer<
    typeof schedulesRollforwardCellSchema
>;

export const schedulesWaterfallCellSchema = z
    .object({
        recognized: z.number(),
        isPosted: z.boolean(),
    })
    .nullable();
export type SchedulesWaterfallCell = z.infer<
    typeof schedulesWaterfallCellSchema
>;

export const schedulesWaterfallSummarySchema = z.object({
    booked: z.number(),
    recognized: z.number(),
    remaining: z.number(),
});
export type SchedulesWaterfallSummary = z.infer<
    typeof schedulesWaterfallSummarySchema
>;

export const schedulesReportDrillSchema = z.object({
    holdingAccountId: z.string().uuid().optional(),
    holdingAccountCode: z.number().int().optional(),
    scheduleApplicationIds: z.array(z.string().uuid()),
    templateId: z.string().uuid().optional(),
    counterpartyId: z.string().uuid().nullable().optional(),
});
export type SchedulesReportDrill = z.infer<typeof schedulesReportDrillSchema>;

const schedulesReportRowBaseSchema = z.object({
    key: z.string(),
    rowKind: z.nativeEnum(SchedulesReportRowKind),
    label: z.string(),
    termStart: z.string().nullable().optional(),
    termEnd: z.string().nullable().optional(),
    revenueSubMode: z.nativeEnum(RevenueSubMode).optional(),
    templateId: z.string().uuid().optional(),
    scheduleApplicationId: z.string().uuid().optional(),
    counterpartyId: z.string().uuid().nullable().optional(),
    counterpartyName: z.string().nullable().optional(),
    drill: schedulesReportDrillSchema.optional(),
    rollforward: z.array(schedulesRollforwardCellSchema).optional(),
    waterfall: z.array(schedulesWaterfallCellSchema).optional(),
    summary: schedulesWaterfallSummarySchema.optional(),
});

export type SchedulesReportRow = z.infer<
    typeof schedulesReportRowBaseSchema
> & {
    children?: SchedulesReportRow[];
};

export const schedulesReportRowSchema: z.ZodType<SchedulesReportRow> =
    schedulesReportRowBaseSchema.extend({
        children: z.lazy(() => z.array(schedulesReportRowSchema)).optional(),
    });

export const schedulesReportHoldingAccountSchema = z.object({
    id: z.string().uuid(),
    code: z.number().int(),
    name: z.string(),
    revenueSubMode: z.nativeEnum(RevenueSubMode),
});
export type SchedulesReportHoldingAccount = z.infer<
    typeof schedulesReportHoldingAccountSchema
>;

export const schedulesReportDateBoundsSchema = z.object({
    minTermStart: z.string().nullable(),
    maxTermEnd: z.string().nullable(),
});
export type SchedulesReportDateBounds = z.infer<
    typeof schedulesReportDateBoundsSchema
>;

export const schedulesReportMetaSchema = z.object({
    appliedLayout: z.nativeEnum(SchedulesReportLayout),
    appliedCycle: z.nativeEnum(AccountingReportCycle).nullable(),
    appliedRows: z.nativeEnum(SchedulesReportRows),
    appliedRevenueSubMode: z.nativeEnum(SchedulesReportRevenueSubMode),
    pendingPeriodCount: z.number().int(),
    holdingAccounts: z.array(schedulesReportHoldingAccountSchema),
    showsCurrentNonCurrent: z.boolean(),
    dateBounds: schedulesReportDateBoundsSchema,
    footnotes: z.array(z.string()),
});
export type SchedulesReportMeta = z.infer<typeof schedulesReportMetaSchema>;

export const schedulesReportResponseSchema = z.object({
    periods: z.array(reportPeriodWithLabelSchema),
    rows: z.array(schedulesReportRowSchema),
    meta: schedulesReportMetaSchema,
    presentation: reportPresentationSchema.optional(),
});
export type SchedulesReportResponse = z.infer<
    typeof schedulesReportResponseSchema
>;

export const schedulesReportResponseWithPresentationSchema =
    withRequiredPresentation(schedulesReportResponseSchema);
export type SchedulesReportResponseWithPresentation = z.infer<
    typeof schedulesReportResponseWithPresentationSchema
>;

export const schedulesReportDateBoundsResponseSchema =
    schedulesReportDateBoundsSchema;
